import { ChangeEvent, FunctionComponent, useContext, useEffect, useState } from 'react'
import styled, { ThemeContext } from 'styled-components'

import useDebounce from 'utils/useDebounce'

interface IContainerProps {
  height?: number
}
const Container = styled.div<IContainerProps>`
  position: relative;
  width: 100%;
  height: ${props => props.height || 250}px;
`

interface IStyledTextAreaProps {
  background?: string
  requiredMissingField?: boolean
  submissionError?: boolean
}

const StyledTextArea = styled.textarea<IStyledTextAreaProps>`
  resize: none;
  padding: 20px;
  width: 100%;
  height: 100%;
  background-color: ${props => props.background || props.theme.colors.grey2};
  border: 1px solid ${props => props.theme.colors.inputBorder};
  border: ${props => `${props.submissionError ? 2 : 1}px solid ${
    props.theme.colors[props.submissionError ? 'red' : 'grey3']
  }
  `};
  ${props =>
    props.requiredMissingField
    && `
    border: 1px solid ${props.theme.colors.red};
    box-shadow: 0px 0px 4px rgba(231, 58, 62, 0.5);
    background: ${props.theme.colors.red4};
  `}

  box-sizing: border-box;

  border-radius: 4px;

  font-style: normal;
  font-weight: 500;
  font-size: 15px;
  line-height: 22px;

  &:focus,
  &:active {
    outline-style: none;
    border: 2px solid ${props => props.theme.colors.blue};
  }
`

interface IMaxCharsProps {
  maxChars: boolean
  outside?: boolean
}
const CharsRemaining = styled.div<IMaxCharsProps>`
  position: absolute;
  bottom: 15px;
  right: 15px;
  ${props =>
    props.outside
    && `
    bottom: -25px;
    right: 0;
  `}
  text-align: right;
  font-style: normal;
  font-weight: 800;
  font-size: 14px;
  line-height: 19px;
  color: ${props => props.theme.colors[props.maxChars ? 'red' : 'transparentBlackAlpha50']};
`
interface IProps {
  background?: string
  name: string
  defaultValue?: string
  delay?: number
  height?: number
  hideCharsRemaining?: boolean
  maxCharsLength?: number
  placeholder?: string
  requiredMissingField?: boolean
  submissionError?: boolean
  showCharsRemainingOutside?: boolean
  onBlur?(): void
  onChange(field: string, value: string | number | boolean | null | undefined): void
}

const TextArea: FunctionComponent<IProps> = ({
  background,
  name,
  defaultValue,
  delay = 1000,
  height,
  hideCharsRemaining,
  maxCharsLength,
  placeholder,
  requiredMissingField,
  submissionError,
  showCharsRemainingOutside,
  onBlur,
  onChange,
}) => {
  const theme = useContext(ThemeContext)

  const [initialized, setInitialized] = useState(false)
  const [value, setValue] = useState(defaultValue || '')

  useEffect(() => {
    if (defaultValue) setValue(defaultValue)
  }, [defaultValue])

  const debouncedValue = useDebounce(value, delay)

  useEffect(() => {
    if (initialized && (debouncedValue || debouncedValue === '')) {
      onChange(name, debouncedValue)
    }
  }, [debouncedValue])

  const updateValue = (event: ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = event?.target?.value
    setInitialized(true)
    setValue(newValue)
  }

  // max character length
  const maxNumberOfCharacters = maxCharsLength || theme.metrics.maxTextAreaCharacters
  const charsRemaining = maxNumberOfCharacters - value.length

  return (
    <Container height={height}>
      <StyledTextArea
        data-testid={name}
        value={value}
        placeholder={placeholder || 'Write your description here...'}
        background={background}
        submissionError={submissionError}
        maxLength={maxNumberOfCharacters}
        onBlur={onBlur}
        onChange={updateValue}
        requiredMissingField={requiredMissingField}
      />
      {value && !hideCharsRemaining && (
        <CharsRemaining
          maxChars={charsRemaining === 0}
          outside={showCharsRemainingOutside}
        >
          {`${charsRemaining} characters remaining`}
        </CharsRemaining>
      )}
    </Container>
  )
}

export default TextArea
